import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import TrackingMap from "../components/TrackingMap";

interface Location {
  id: string;
  name: string;
  nameEn: string;
  status: 'active' | 'pending' | 'delivered';
  type: 'warehouse' | 'order' | 'driver' | 'customer';
  lat: number;
  lon: number;
  lastUpdate: string;
}

const LocationTracking: React.FC = () => {
  const { t } = useLanguage();
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  const locations: Location[] = [
    { id: '1', name: 'Bangkok', nameEn: 'Bangkok', status: 'active', type: 'warehouse', lat: 13.7563, lon: 100.5018, lastUpdate: '2 minutes ago' },
    { id: '2', name: 'Chiang Mai', nameEn: 'Chiang Mai', status: 'pending', type: 'order', lat: 18.7883, lon: 98.9853, lastUpdate: '5 minutes ago' },
    { id: '3', name: 'Phuket', nameEn: 'Phuket', status: 'delivered', type: 'order', lat: 7.8804, lon: 98.3923, lastUpdate: '1 hour ago' },
    { id: '4', name: 'Pattaya', nameEn: 'Pattaya', status: 'active', type: 'driver', lat: 12.9236, lon: 100.8825, lastUpdate: '1 minute ago' },
    { id: '5', name: 'Hat Yai', nameEn: 'Hat Yai', status: 'pending', type: 'order', lat: 7.0086, lon: 100.4747, lastUpdate: '10 minutes ago' },
    { id: '6', name: 'Nakhon Ratchasima', nameEn: 'Nakhon Ratchasima', status: 'active', type: 'customer', lat: 14.9799, lon: 102.0978, lastUpdate: '3 minutes ago' },
    { id: '7', name: 'Khon Kaen', nameEn: 'Khon Kaen', status: 'pending', type: 'warehouse', lat: 16.4322, lon: 102.8236, lastUpdate: '15 minutes ago' },
    { id: '8', name: 'Surat Thani', nameEn: 'Surat Thani', status: 'active', type: 'driver', lat: 9.1382, lon: 99.3219, lastUpdate: '4 minutes ago' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'delivered': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-400';
      case 'pending': return 'bg-yellow-400';
      case 'delivered': return 'bg-blue-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'In Progress';
      case 'pending': return 'Pending';
      case 'delivered': return 'Delivered';
      default: return status;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'warehouse': return 'W';
      case 'driver': return 'D';
      case 'order': return 'O';
      case 'customer': return 'C';
      default: return 'L';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'warehouse': return 'text-purple-400';
      case 'driver': return 'text-blue-400';
      case 'order': return 'text-green-400';
      case 'customer': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  const activeCount = locations.filter(l => l.status === 'active').length;
  const pendingCount = locations.filter(l => l.status === 'pending').length;
  const deliveredCount = locations.filter(l => l.status === 'delivered').length;

  return (
    <div className="min-h-screen bg-white">
      <main className="p-6">
        <div className="space-y-8">
          {/* Header */}
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Location Tracking</h2>
            <p className="text-sm text-gray-600 mt-1">Real-time tracking of deliveries, warehouses, and drivers</p>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Summary Statistics</h3>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-xs text-gray-500">Real-time</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200 hover:bg-gray-100 transition-colors">
                  <div className="text-2xl font-bold text-green-600">{activeCount}</div>
                  <div className="text-xs text-gray-600">In Progress</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200 hover:bg-gray-100 transition-colors">
                  <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
                  <div className="text-xs text-gray-600">Pending</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200 hover:bg-gray-100 transition-colors">
                  <div className="text-2xl font-bold text-blue-600">{deliveredCount}</div>
                  <div className="text-xs text-gray-600">Delivered</div>
                </div>
              </div>
            </div>

            {/* Map View */}
            <div className="lg:col-span-3 card">
              <h3 className="font-semibold text-gray-900 mb-4">Live Map View</h3>
              <div className="h-96 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-2">Map</div>
                  <p className="text-gray-600">Interactive map showing all locations</p>
                  {selectedLocation && (
                    <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                      <p className="text-gray-900 font-medium">{selectedLocation.name}</p>
                      <p className="text-gray-600 text-sm">Lat: {selectedLocation.lat}, Lon: {selectedLocation.lon}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Location List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">Active Locations</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {locations.filter(l => l.status === 'active').map((location) => (
                  <div
                    key={location.id}
                    onClick={() => setSelectedLocation(location)}
                    className="group flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-3 h-3 rounded-full ${getStatusBgColor(location.status)} flex-shrink-0`} />
                      <div className={`w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold ${getTypeColor(location.type)}`}>
                        {getTypeIcon(location.type)}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm text-gray-900 font-medium truncate">{location.name}</span>
                        <span className="text-xs text-gray-500 truncate">{location.nameEn}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <div className={`text-xs font-medium ${getStatusColor(location.status)}`}>{getStatusText(location.status)}</div>
                        <div className="text-xs text-gray-400">{location.lastUpdate}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">All Locations</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {locations.map((location) => (
                  <div
                    key={location.id}
                    onClick={() => setSelectedLocation(location)}
                    className="group flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-3 h-3 rounded-full ${getStatusBgColor(location.status)} flex-shrink-0`} />
                      <div className={`w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold ${getTypeColor(location.type)}`}>
                        {getTypeIcon(location.type)}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm text-gray-900 font-medium truncate">{location.name}</span>
                        <span className="text-xs text-gray-500 truncate">{location.nameEn}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <div className={`text-xs font-medium ${getStatusColor(location.status)}`}>{getStatusText(location.status)}</div>
                        <div className="text-xs text-gray-400">{location.lastUpdate}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LocationTracking;
